/** Kokochao V15 HORIZON FINAL — Dwell-style Collection + Search facets on Horizon SectionRenderer. */
import { sectionRenderer } from '@theme/section-renderer';

const CATALOG_SELECTOR = '[data-kc-h15-catalog]';
const FILTER_FORM_SELECTOR = '[data-kc-h15-filter-form]';
const SORT_SELECTOR = '[data-kc-h15-sort]';
const FILTER_LINK_SELECTOR = '[data-kc-h15-filter-link]';
const PAGINATION_SELECTOR = '[data-kc-h15-pagination] a';

let updateTimer = 0;

function root() {
  return document.querySelector(CATALOG_SELECTOR);
}

function sectionId() {
  return root()?.dataset.sectionId || '';
}

function normalizeTargetUrl(value) {
  return new URL(value, window.location.origin);
}

function deleteFilterParams(url) {
  for (const key of [...url.searchParams.keys()]) {
    if (key.startsWith('filter.')) url.searchParams.delete(key);
  }
}

function appendFormFilters(url, form) {
  deleteFilterParams(url);
  const data = new FormData(form);
  for (const [name, value] of data.entries()) {
    if (!name.startsWith('filter.')) continue;
    const clean = String(value).trim();
    if (clean !== '') url.searchParams.append(name, clean);
  }
}

function urlFromFilterForm(form) {
  const url = new URL(window.location.href);
  appendFormFilters(url, form);
  url.searchParams.delete('page');
  return url;
}

function urlFromSort(select) {
  const url = new URL(window.location.href);
  if (select.value) url.searchParams.set('sort_by', select.value);
  else url.searchParams.delete('sort_by');
  url.searchParams.delete('page');
  return url;
}

function setBusy(isBusy) {
  const el = root();
  if (!el) return;
  el.setAttribute('aria-busy', isBusy ? 'true' : 'false');
}

async function renderUrl(url, { push = true, scrollToResults = false } = {}) {
  const storefrontUrl = new URL(url.toString());
  const id = sectionId();
  if (!id) {
    window.location.assign(storefrontUrl.toString());
    return;
  }

  setBusy(true);

  const render = async () => {
    // SectionRenderer appends section_id to its URL object; pass a clone so the public URL stays canonical.
    const renderRequestUrl = new URL(storefrontUrl.toString());
    await sectionRenderer.renderSection(id, {
      cache: false,
      mode: 'full',
      url: renderRequestUrl,
    });
  };

  try {
    if (document.startViewTransition && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      await document.startViewTransition(render).finished;
    } else {
      await render();
    }

    if (push) history.pushState({ kcH15Catalog: true }, '', storefrontUrl);

    if (scrollToResults) {
      document.querySelector('[data-kc-h15-results]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  } catch (error) {
    if (error?.name === 'AbortError') return;
    console.warn('[Kokochao V15] Catalog rendering fallback:', error);
    window.location.assign(storefrontUrl.toString());
  } finally {
    setBusy(false);
  }
}

function closeMobileDrawer() {
  const drawer = document.querySelector('theme-drawer[data-kc-h15-filter-drawer][open], theme-drawer[data-kc-h15-sort-drawer][open]') || document.querySelector('theme-drawer[data-kc-h15-filter-drawer], theme-drawer[data-kc-h15-sort-drawer]');
  if (drawer?.isOpen && typeof drawer.close === 'function') drawer.close();
}

function scheduleDesktopUpdate(form) {
  window.clearTimeout(updateTimer);
  updateTimer = window.setTimeout(() => renderUrl(urlFromFilterForm(form)), 160);
}

document.addEventListener(
  'change',
  (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    if (target.matches(`${FILTER_FORM_SELECTOR}[data-mode="desktop"] input`)) {
      const form = target.closest(FILTER_FORM_SELECTOR);
      if (form instanceof HTMLFormElement) scheduleDesktopUpdate(form);
      return;
    }

    if (target.matches(SORT_SELECTOR)) {
      if (target instanceof HTMLSelectElement) {
        closeMobileDrawer();
        renderUrl(urlFromSort(target));
      }
    }
  },
  true,
);

document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches(FILTER_FORM_SELECTOR)) return;
    event.preventDefault();
    event.stopImmediatePropagation();

    const url = urlFromFilterForm(form);
    closeMobileDrawer();
    renderUrl(url, { push: true });
  },
  true,
);

document.addEventListener(
  'click',
  (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const filterLink = target.closest(FILTER_LINK_SELECTOR);
    if (filterLink instanceof HTMLAnchorElement) {
      event.preventDefault();
      event.stopImmediatePropagation();
      closeMobileDrawer();
      renderUrl(normalizeTargetUrl(filterLink.href));
      return;
    }

    const pageLink = target.closest(PAGINATION_SELECTOR);
    if (pageLink instanceof HTMLAnchorElement) {
      event.preventDefault();
      renderUrl(normalizeTargetUrl(pageLink.href), { push: true, scrollToResults: true });
    }
  },
  true,
);

window.addEventListener('popstate', () => {
  if (!root()) return;
  renderUrl(new URL(window.location.href), { push: false });
});
